import React, { useEffect, useMemo, useState } from 'react';
import { IconCheck, IconSearch } from '../Icons';
import { fetchSnaptradeAvailability, fetchSnaptradeBrokerages, snaptradeConnectLink } from '../../services/vectorDashboardApi';
import { COMING_SOON_BROKERS } from '../welcome/connectSources';
import './BrokerPicker.css';

/**
 * Every broker in one searchable grid: RiskLo's direct connections (Coinbase,
 * Alpaca, Interactive Brokers, NinjaTrader) and everything linkable through
 * SnapTrade (Robinhood, Schwab, Webull, Fidelity…).
 *
 * Direct brokers open their own setup. SnapTrade brokers open SnapTrade's
 * sign-in for that broker, or the manage panel once already linked.
 */

// Popular first (direct and SnapTrade mixed), then A–Z.
const ORDER = ['ROBINHOOD', 'SCHWAB', 'ninjatrader', 'coinbase', 'ibkr', 'WEBULL', 'FIDELITY', 'alpaca',
  'ETRADE', 'VANGUARD', 'PUBLIC', 'TASTYTRADE', 'MOOMOO', 'CHASE', 'WELLS-FARGO', 'KRAKEN', 'BINANCE', 'ETORO'];

let cached = null; // SnapTrade list for this page load
let cachedAvailable = null; // server has a SnapTrade key (null = not known)

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function BrokerPicker({ directBrokers = [], brokerStatus = {}, onOpenDirect, onManageLinked, returnTo = null }) {
  const [linkable, setLinkable] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState('');
  const [error, setError] = useState('');

  const [snaptradeAvailable, setSnaptradeAvailable] = useState(cachedAvailable);
  useEffect(() => {
    if (cachedAvailable !== null) return undefined;
    let live = true;
    fetchSnaptradeAvailability()
      .then((res) => { cachedAvailable = res?.available !== false; if (live) setSnaptradeAvailable(cachedAvailable); })
      .catch(() => { /* not known: no Coming soon tiles */ });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (cached) return undefined;
    let live = true;
    fetchSnaptradeBrokerages()
      .then((res) => { cached = res.brokerages || []; if (live) setLinkable(cached); })
      .catch(() => { cached = []; }) // SnapTrade off: direct brokers only
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  // Institutions already linked through SnapTrade (e.g. "Robinhood").
  const linkedNames = useMemo(
    () => new Set((brokerStatus.snaptrade?.accounts || []).map((a) => norm(a.institution)).filter(Boolean)),
    [brokerStatus.snaptrade],
  );

  const items = useMemo(() => {
    const direct = directBrokers.map((b) => ({
      key: b.id,
      kind: 'direct',
      id: b.id,
      name: b.label,
      logo: b.logoSrc || null,
      note: b.description,
      connected: Boolean(brokerStatus[b.id]?.configured),
    }));
    const viaSnaptrade = linkable.map((b) => ({
      key: b.slug,
      kind: 'snaptrade',
      slug: b.slug,
      name: b.name,
      logo: b.logo,
      crypto: b.crypto,
      maintenance: b.maintenance,
      connected: [...linkedNames].some((n) => n && (norm(b.name).startsWith(n) || n.startsWith(norm(b.name)))),
    }));
    // No SnapTrade key on this server: still show the popular brokerages,
    // marked "Coming soon", rather than an empty picker.
    // Only when the server has no SnapTrade key (not when the list is slow or empty).
    const soon = snaptradeAvailable === false && !linkable.length
      ? COMING_SOON_BROKERS.map((b) => ({ key: b.slug, kind: 'soon', slug: b.slug, name: b.title, logo: b.logo, connected: false }))
      : [];
    const rank = (it) => {
      const i = ORDER.indexOf(it.kind === 'direct' ? it.id : it.slug);
      return i === -1 ? 999 : i;
    };
    return [...direct, ...viaSnaptrade, ...soon].sort((a, b) => (
      (Number(b.connected) - Number(a.connected))
      || (Number(a.kind === 'soon') - Number(b.kind === 'soon'))
      || (rank(a) - rank(b)) || a.name.localeCompare(b.name)
    ));
  }, [directBrokers, brokerStatus, linkable, linkedNames, snaptradeAvailable]);

  const shown = useMemo(() => {
    const q = norm(query);
    if (!q) return items;
    return items.filter((it) => norm(it.name).includes(q) || norm(it.slug).includes(q) || norm(it.note).includes(q));
  }, [items, query]);

  const choose = async (it) => {
    if (opening || it.kind === 'soon') return;
    if (it.kind === 'direct') { onOpenDirect?.(it.id); return; }
    if (it.connected) { onManageLinked?.(); return; }
    setOpening(it.slug);
    setError('');
    try {
      const light = document.body.classList.contains('light-mode');
      const res = await snaptradeConnectLink({ broker: it.slug, darkMode: !light, returnTo });
      if (!res?.url) throw new Error('No link returned');
      window.location.assign(res.url);
    } catch (_e) {
      setOpening('');
      setError(`Couldn’t open ${it.name}. Try again in a moment.`);
    }
  };

  return (
    <div className="bp">
      <label className="bp-search">
        <IconSearch size={16} />
        <input
          type="search"
          placeholder={`Search ${items.length || ''} brokers`.replace('  ', ' ')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search brokers"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </label>

      {error ? <p className="bp-error" role="alert">{error}</p> : null}

      <div className="bp-grid">
        {shown.map((it) => (
          <button
            key={it.key}
            type="button"
            className={`bp-tile${it.connected ? ' is-connected' : ''}${opening === it.slug ? ' is-opening' : ''}${it.kind === 'soon' ? ' is-soon' : ''}`}
            onClick={() => choose(it)}
            disabled={Boolean(opening) && opening !== it.slug}
            aria-disabled={it.kind === 'soon' || undefined}
            title={it.kind === 'soon' ? `${it.name} is coming soon`
              : it.maintenance ? `${it.name} is under maintenance at SnapTrade` : it.connected ? `Manage ${it.name}` : `Connect ${it.name}`}
          >
            <span className="bp-logo">
              {it.logo ? <img src={it.logo} alt="" loading="lazy" /> : <span>{it.name.charAt(0)}</span>}
            </span>
            <span className="bp-name">{it.name}</span>
            {it.connected ? <span className="bp-status"><IconCheck size={12} /> Connected</span> : null}
            {it.kind === 'soon' ? <span className="bp-tag is-soon">Coming soon</span>
              : opening === it.slug ? <span className="bp-tag">Opening…</span>
              : it.maintenance ? <span className="bp-tag is-warn">Maintenance</span>
                : it.crypto ? <span className="bp-tag">Crypto</span> : null}
          </button>
        ))}
        {loading ? Array.from({ length: 6 }).map((_, i) => <div key={`s${i}`} className="bp-tile is-skeleton" />) : null}
      </div>

      {!loading && !shown.length ? <p className="bp-empty">No broker matches &ldquo;{query}&rdquo;.</p> : null}
      <p className="bp-foot">
        Read-only connections. You sign in on your broker&rsquo;s own page, never in RiskLo. Brokerages linked
        through SnapTrade show up to 12 months of past daily account values.
      </p>
    </div>
  );
}
